package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.rotatesWhenOpen
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideSearch
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.shared.ui.ListboxAction
import co.maytes.standupbot.shared.ui.ListboxOption
import co.maytes.standupbot.shared.ui.ListboxReducer
import co.maytes.standupbot.shared.ui.ListboxState
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.onMouseDown
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.events.SyntheticKeyboardEvent
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.InputType
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.dom.Button as ButtonElement

private const val LISTBOX_MAX_HEIGHT_PX = 280
private const val NO_MATCHES_TEXT = "No matches"
private const val SEARCH_LABEL = "Search options"

private fun nowMs(): Long = window.performance.now().toLong()

private fun isPrintable(key: String, ctrl: Boolean, meta: Boolean, alt: Boolean): Boolean =
    key.length == 1 && !ctrl && !meta && !alt

private fun scrollOptionIntoView(id: String) {
    val element = document.getElementById(id) as? HTMLElement ?: return
    val options: dynamic = js("({})")
    options.block = "nearest"
    element.asDynamic().scrollIntoView(options)
}

@Composable
internal fun <T> ListboxSelect(
    value: T,
    options: List<SelectOption<T>>,
    onValueChange: (T) -> Unit,
    metrics: ControlMetrics,
    field: FieldControlProps,
    searchable: Boolean,
    disabled: Boolean,
    loading: Boolean,
    placeholder: String?,
    ariaLabel: String?,
) {
    val listId = remember(field.id) { "${field.id}-list" }
    val model = remember(options) { options.map { ListboxOption(it.label, it.disabled) } }
    val selectedIndex = options.indexOfFirst { it.value == value }
    var state by remember { mutableStateOf(ListboxState()) }
    val view = state
    val triggerHolder = remember { ElementHolder() }

    fun dispatch(action: ListboxAction) {
        state = ListboxReducer.reduce(model, state, action)
    }

    fun open() = dispatch(ListboxAction.Open(selectedIndex.takeIf { it >= 0 }))

    fun close() = dispatch(ListboxAction.Close)

    fun choose(index: Int) {
        val option = options.getOrNull(index) ?: return
        if (option.disabled) return
        onValueChange(option.value)
        close()
    }

    fun commit() {
        ListboxReducer.commitIndex(model, state)?.let { choose(it) }
    }

    fun typeOnClosedTrigger(char: Char) {
        if (searchable) {
            dispatch(ListboxAction.SetQuery(char.toString()))
        } else {
            val opened = ListboxReducer.reduce(model, state, ListboxAction.Open(selectedIndex.takeIf { it >= 0 }))
            state = ListboxReducer.reduce(model, opened, ListboxAction.TypeAhead(char, nowMs()))
        }
    }

    val activeId = view.active?.let { ListboxReducer.optionId(listId, it) }
    val slots = if (field.invalid) 2 else 1

    LaunchedEffect(activeId, view.open) {
        if (view.open && activeId != null) {
            window.requestAnimationFrame { scrollOptionIntoView(activeId) }
        }
    }

    Div(
        attrs = Modifier
            .styleModifier {
                property("position", "relative")
                property("width", "100%")
            }
            .toAttrs(),
    ) {
        ButtonElement(
            attrs = fieldBoxLook(metrics, slots)
                .styleModifier {
                    property("width", "100%")
                    property("display", "flex")
                    property("align-items", "center")
                    property("text-align", "left")
                    property("cursor", if (disabled) "not-allowed" else "pointer")
                }
                .onClick { if (!disabled) { if (state.open) close() else open() } }
                .onKeyDown { event ->
                    if (disabled) return@onKeyDown
                    val key = event.key
                    if (!state.open) {
                        when {
                            key == "ArrowDown" || key == "ArrowUp" || key == "Enter" || key == " " -> {
                                event.preventDefault()
                                open()
                            }
                            isPrintable(key, event.ctrlKey, event.metaKey, event.altKey) -> {
                                event.preventDefault()
                                typeOnClosedTrigger(key[0])
                            }
                        }
                    } else {
                        when {
                            key == "ArrowDown" -> { event.preventDefault(); dispatch(ListboxAction.Move(1)) }
                            key == "ArrowUp" -> { event.preventDefault(); dispatch(ListboxAction.Move(-1)) }
                            key == "Home" -> { event.preventDefault(); dispatch(ListboxAction.First) }
                            key == "End" -> { event.preventDefault(); dispatch(ListboxAction.Last) }
                            key == " " && !searchable && ListboxReducer.isTyping(state, nowMs()) -> {
                                event.preventDefault()
                                dispatch(ListboxAction.TypeAhead(' ', nowMs()))
                            }
                            key == "Enter" || key == " " -> { event.preventDefault(); commit() }
                            key == "Tab" -> close()
                            !searchable && isPrintable(key, event.ctrlKey, event.metaKey, event.altKey) -> {
                                event.preventDefault()
                                dispatch(ListboxAction.TypeAhead(key[0], nowMs()))
                            }
                        }
                    }
                }
                .attrsModifier {
                    if (!searchable) attr("role", "combobox")
                    if (!searchable && activeId != null) attr("aria-activedescendant", activeId)
                }
                .toAttrs {
                    attr("type", "button")
                    attr("aria-haspopup", "listbox")
                    attr("aria-expanded", view.open.toString())
                    attr("aria-controls", listId)
                    if (disabled) attr("disabled", "")
                    if (loading) attr("aria-busy", "true")
                    ariaLabel?.let { attr("aria-label", it) }
                    fieldControl(field)
                    ref { element ->
                        triggerHolder.element = element
                        onDispose { if (triggerHolder.element === element) triggerHolder.element = null }
                    }
                },
        ) {
            val selectedLabel = options.getOrNull(selectedIndex)?.label
            Span(
                attrs = Modifier
                    .styleModifier {
                        if (selectedLabel == null) property("color", Palette.textTertiary.toString())
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
                    .toAttrs(),
            ) { Text(selectedLabel ?: placeholder.orEmpty()) }
        }
        FieldAdornments(metrics.padX) {
            if (field.invalid) InvalidAdornment()
            AdornmentSlot {
                if (loading) {
                    Spinner(sizePx = AdornmentIconPx, ariaLabel = "")
                } else {
                    Span(attrs = Modifier.rotatesWhenOpen(view.open).toAttrs()) {
                        LucideChevronDown(size = AdornmentIconPx)
                    }
                }
            }
        }

        Popover(
            open = view.open,
            placement = PopoverPlacement.BottomFill,
            onDismiss = { close() },
            modifier = Modifier
                .classNames("sb-surface")
                .borderRadius(Radius.surface)
                .styleModifier {
                    property("border", "1px solid")
                    property("box-shadow", Shadow.MD)
                    property("overflow", "hidden")
                },
        ) {
            if (searchable) {
                SearchRow(
                    metrics = metrics,
                    query = view.query,
                    listId = listId,
                    activeId = activeId,
                    onQuery = { dispatch(ListboxAction.SetQuery(it)) },
                    onKey = { event ->
                        when (event.key) {
                            "ArrowDown" -> { event.preventDefault(); dispatch(ListboxAction.Move(1)) }
                            "ArrowUp" -> { event.preventDefault(); dispatch(ListboxAction.Move(-1)) }
                            "Home" -> { event.preventDefault(); dispatch(ListboxAction.First) }
                            "End" -> { event.preventDefault(); dispatch(ListboxAction.Last) }
                            "Enter" -> { event.preventDefault(); commit() }
                            "Tab" -> close()
                        }
                    },
                )
            }
            OptionList(
                listId = listId,
                options = options,
                view = view,
                selectedIndex = selectedIndex,
                metrics = metrics,
                labelledBy = if (field.labelled) field.ids.label else null,
                ariaLabel = ariaLabel,
                onHighlight = { dispatch(ListboxAction.Highlight(it)) },
                onChoose = { choose(it) },
            )
        }
    }
}

@Composable
private fun SearchRow(
    metrics: ControlMetrics,
    query: String,
    listId: String,
    activeId: String?,
    onQuery: (String) -> Unit,
    onKey: (SyntheticKeyboardEvent) -> Unit,
) {
    Div(
        attrs = Modifier
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "${Space.s2}")
                property("padding", "0 ${metrics.padX}")
                property("border-bottom", "1px solid ${Palette.border}")
                property("color", Palette.textTertiary.toString())
            }
            .toAttrs(),
    ) {
        LucideSearch(size = AdornmentIconPx)
        Input(
            type = InputType.Text,
            attrs = Modifier
                .classNames(ControlClass.SELECT_SEARCH)
                .fontSize(metrics.fontSize)
                .styleModifier { property("height", "${metrics.height}") }
                .onKeyDown { onKey(it) }
                .toAttrs {
                    value(query)
                    attr("role", "combobox")
                    attr("aria-label", SEARCH_LABEL)
                    attr("aria-expanded", "true")
                    attr("aria-controls", listId)
                    attr("aria-autocomplete", "list")
                    attr("autocomplete", "off")
                    attr("spellcheck", "false")
                    if (activeId != null) attr("aria-activedescendant", activeId)
                    onInput { onQuery(it.value) }
                },
        )
    }
}

@Composable
private fun <T> OptionList(
    listId: String,
    options: List<SelectOption<T>>,
    view: ListboxState,
    selectedIndex: Int,
    metrics: ControlMetrics,
    labelledBy: String?,
    ariaLabel: String?,
    onHighlight: (Int) -> Unit,
    onChoose: (Int) -> Unit,
) {
    Div(
        attrs = Modifier
            .styleModifier {
                property("max-height", "${LISTBOX_MAX_HEIGHT_PX}px")
                property("overflow-y", "auto")
                property("padding", "${Space.s1}")
            }
            .toAttrs {
                attr("id", listId)
                attr("role", "listbox")
                attr("tabindex", "-1")
                if (labelledBy != null) attr("aria-labelledby", labelledBy) else attr("aria-label", ariaLabel ?: SEARCH_LABEL)
            },
    ) {
        if (view.visible.isEmpty()) {
            Div(
                attrs = Modifier
                    .fontSize(metrics.fontSize)
                    .color(Palette.textTertiary)
                    .styleModifier { property("padding", "${Space.s2} ${metrics.padX}") }
                    .toAttrs { attr("role", "status") },
            ) { Text(NO_MATCHES_TEXT) }
        }
        view.visible.forEach { index ->
            val option = options[index]
            key(index) {
                Div(
                    attrs = Modifier
                        .classNames(ControlClass.OPTION)
                        .fontSize(metrics.fontSize)
                        .styleModifier {
                            property("min-height", "${metrics.height}")
                            property("padding", "0 ${metrics.padX}")
                        }
                        .onMouseEnter { onHighlight(index) }
                        .onMouseDown { event -> event.preventDefault() }
                        .onClick { onChoose(index) }
                        .toAttrs {
                            attr("id", ListboxReducer.optionId(listId, index))
                            attr("role", "option")
                            attr("aria-selected", (index == selectedIndex).toString())
                            if (option.disabled) attr("aria-disabled", "true")
                            if (index == view.active) attr("data-active", "true")
                        },
                ) {
                    Span(
                        attrs = Modifier
                            .styleModifier {
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                            .toAttrs(),
                    ) { Text(option.label) }
                    if (index == selectedIndex) {
                        LucideCheck(size = AdornmentIconPx, strokeColor = Palette.accent.toString())
                    }
                }
            }
        }
    }
}
